import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { useMounted } from '@restworld/utils-common';

import { jobListParamsAtom } from '../job-explorer-list.atoms';
import { jobCoordinateParamsAtom } from '../job-explorer-map.atoms';
import { filterContextValuesAtom, moreFiltersAtom } from '../job-explorer-filters.atoms';
import { serverListPayloadAtom, serverCoordinatePayloadAtom } from '../job-explorer-server.atoms';
import { FilterContextValuesType } from '../job-explorer.types';

const CONTRACT_TYPE = 'contract_type';
const SEASONAL_ACCOMMODATION = 'seasonal_accommodation';
const DOMAIN_INTEREST = 'domain_interest';

export const useExplorerInitialApiCalls = () => {
  const isMounted = useMounted();
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const setJobCoordinateParams = useSetAtom(jobCoordinateParamsAtom);
  const serverListPayload = useAtomValue(serverListPayloadAtom);
  const serverCoordinatePayload = useAtomValue(serverCoordinatePayloadAtom);
  const setMoreFilters = useSetAtom(moreFiltersAtom);
  const setFilterContextValues = useSetAtom(filterContextValuesAtom);

  useEffect(() => {
    if (!isMounted || !serverListPayload) return;

    const contractTypeArr = serverListPayload[CONTRACT_TYPE];
    const hasSeasonalAccommodation = Array.isArray(contractTypeArr) && contractTypeArr[0] === SEASONAL_ACCOMMODATION;

    if (hasSeasonalAccommodation) {
      const updateWithSeasonal = (prev: FilterContextValuesType) => ({
        ...prev,
        [CONTRACT_TYPE]: prev?.[CONTRACT_TYPE]
          ? [...prev[CONTRACT_TYPE], SEASONAL_ACCOMMODATION]
          : [SEASONAL_ACCOMMODATION],
      });
      setFilterContextValues(updateWithSeasonal);
      setMoreFilters(updateWithSeasonal);
      return;
    }

    if (DOMAIN_INTEREST in serverListPayload) {
      const updateWithDomainInterest = (prev: FilterContextValuesType) => ({
        ...prev,
        ...serverListPayload,
      });
      setFilterContextValues(updateWithDomainInterest);
      setMoreFilters(updateWithDomainInterest);
    }
  }, [isMounted, serverListPayload, setFilterContextValues, setMoreFilters]);

  useEffect(() => {
    if (!isMounted || !serverListPayload) return;

    const newPayload = Object.entries(serverListPayload).reduce((acc, [key, value]) => {
      if (key === CONTRACT_TYPE && Array.isArray(value) && value.length) {
        return { ...acc, [CONTRACT_TYPE]: [...value, ...value] };
      }
      return { ...acc, [key]: value };
    }, {});

    const timeout = setTimeout(() => {
      setJobListParams((prev) => ({ ...prev, ...newPayload }));
    }, 250);

    return () => clearTimeout(timeout);
  }, [isMounted, serverListPayload, setJobListParams]);

  useEffect(() => {
    if (!isMounted || !serverCoordinatePayload) return;
    setJobCoordinateParams((prev) => ({
      ...prev,
      ...serverCoordinatePayload,
    }));
  }, [isMounted, serverCoordinatePayload, setJobCoordinateParams]);
};
